import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';

import { DataTable } from 'primereact/datatable';
import { MultiSelect } from 'primereact/multiselect';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';

import { textFilterTemplate, simpleSelectFilterTemplate } from './filter_templates';
import useQueryFilterParams from '../../hooks/controldoc_datatable/useQueryFilterParams';

import DistributionListFilter from './distribution_list_filter/index';
import axios from 'axios';

import {
  clearButton,
  emptyMessage,
  onDataTableChange,
} from './utils';

const ALLOWED_KEYS = [
  'emptyMessage',
  'filterDisplay',
  'showGridlines',
  'stripedRows',
  'selectionMode',
  'selection',
]

const initializeColumnsFilters = (columns) => {
  const initialFilters = {};
  _.each(columns, (column) => { if (column.field) initialFilters[column.field] = { value: '' }; });
  return initialFilters;
}

const ControldocDataTable = props => {
  const {
    header,
    columns,
  } = props;

  const {
    endpoint: fetchRecordEndpoint,
    keyName: fetchRecordKeyName,
    defaultRequestParams: fetchRecordDefaultRequestParams
  } = props?.configuration?.fetchRecords || {}

  const {
    exportZip,
    exportXlsx,
    columnSelectable,
    customAction
  } = props?.configuration || {};

  const showAllRecordsRef = useRef(false)

  // Almacena los registros de la tabla
  const [records, setRecords] = useState({
    data: [],
    currentPage: 1,
    inPage: 0,
    total: 0,
    totalPages: 0
  })

  // Almacena los parametros de la query
  const [queryFilterParams, setQueryFilterParams] = useQueryFilterParams();

  // Almacena los filtros de la tabla
  const [filters, setFilters] = useState(() => initializeColumnsFilters(columns))

  const [sort, setSort] = useState({})
  const [distributionList, setDistributionList] = useState({ name: '', filters: [] });

  // Sincroniza los filtros con las columnas visibles
  useEffect(() => {
    const intialFilters = initializeColumnsFilters(columns);

    setFilters(intialFilters);

    onDataTableChange({
      type: 'filter',
      filters: intialFilters
    }, columns, setQueryFilterParams);
  }, [columns]);

  // ========== START FETCH RECORDS ==========
  useEffect(() => {
    fetchRecords()
  }, [queryFilterParams])

  useEffect(() => {
    if (_.isFunction(props?.configuration?.onFilterrificChange)) {
      props?.configuration?.onFilterrificChange(queryFilterParams?.filterrific || {});
    }
  }, [queryFilterParams]);

  const fetchRecords = useCallback(() => {
    fetchRecordsServices().then(response => {
      setRecords({
        data: response[fetchRecordKeyName],
        currentPage: response.current_page,
        inPage: response.in_page,
        total: response.total,
        totalPages: response.total_pages
      })

      if (showAllRecordsRef.current) {
        let onSelectionChangeCallback = props?.configuration?.selectable?.onSelectionChange;
        if (_.isFunction(onSelectionChangeCallback)) {
          onSelectionChangeCallback(response[fetchRecordKeyName])
        }

        showAllRecordsRef.current = false;
      }

      if (_.isFunction(props?.configuration?.setTotalRecords)) {
        props?.configuration?.setTotalRecords(response?.total);
      }
    }).catch(error => {
      console.error(error);
    });
  }, [queryFilterParams])

  const fetchRecordsServices = async () => {
    let queryFilteredParams = { ...queryFilterParams }
    queryFilteredParams.filterrific = {
      ...queryFilteredParams.filterrific,
      show_all: showAllRecordsRef.current
    }

    const requestParams = _.merge(
      {},
      fetchRecordDefaultRequestParams,
      queryFilteredParams,
      { format: 'json' }
    );

    const response = await axios.get(fetchRecordEndpoint({ ...requestParams }), {
      headers: {
        'X-CSRF-TOKEN': window.compliance.token,
        'Accept': 'application/json'
      }
    });

    return response.data;
  }
  // ========== END FETCH RECORDS ==========

  // ========== START PAGINATION ==========
  const onPage = event => {
    let _event = { ...event, page: event.page + 1, type: 'pagination' }

    onDataTableChange(
      _event,
      columns,
      setQueryFilterParams
    );
  }
  // ========== END PAGINATION ==========
  // ========================================
  // ========== START FILTERS ==========
  // Initialize filters when columns change
  const onFilter = event => {
    setFilters(event.filters);

    let _event = { ...event, type: 'filter' }
    onDataTableChange(
      _event,
      columns,
      setQueryFilterParams
    );
  }

  const applyFilters = (newFilters) => {
    if (_.isArray(newFilters)) {
      newFilters = formatFilters(newFilters);
    }

    if (!_.isEqual(filters, newFilters)) {
      onFilter({ filters: newFilters });
    }
  }

  const formatFilters = (newFilters) => {
    let _filters = { ...filters };
    _.each(_filters, (value, key) => {
      let newFilter = _.find(newFilters, filter => filter.field == key);
      _filters[key] = { value: newFilter?.value || '' };
    });

    return _filters
  }

  // Generate filter options for each column
  const filterOptions = useCallback((column) => {
    const { filter } = column;
    const filterEnabled = filter?.enabled || !!filter?.element;
    let filterElement = textFilterTemplate;

    switch (filter?.inputType) {
      case 'text':
        filterElement = textFilterTemplate;
        break;
      case 'simple_select':
        filterElement = simpleSelectFilterTemplate;
        break;
    }

    let options = {
      showFilterMatchModes: false,
      showAddButton: false,
      showApplyButton: false,
      showClearButton: true,
      filterClear: clearButton,
      filterMenuStyle: { width: '275px' },
    }

    if (!options.showApplyButton && !options.showClearButton) {
      options.filterMenuClassName += ' p-column-without-buttonbar'
    }

    let filterOptions = {
      ...options,
      filter: filterEnabled,
      filterElement: filterElement ? filterElement(filter) : null,
      // TODO: revisar si se puede usar el filterDisplay de la configuración
      // showFilterMenu: props?.configuration?.filterDisplay === 'menu',
      showFilterMenu: true,
    }

    return filterOptions;
  }, [props?.configuration])
  // ========== END FILTERS ==========
  // ========================================
  // ========== START SORT ==========
  const onSort = event => {
    let _event = { ...event }

    if (event.sortOrder == null || event.sortOrder == undefined || event.sortOrder == 0) {
      _event = {}
    } else {
      _event.order = _event.sortOrder == 1 ? 'asc' : 'desc';
    }

    _event['type'] = 'sort';

    setSort(_event)

    onDataTableChange(
      _event,
      columns,
      setQueryFilterParams
    )
  }

  // Generate sort options for each column
  const sortOptions = useCallback((column) => {
    const { sort } = column;

    const {
      enabled = false
    } = sort || {}

    return {
      sortable: enabled
    }
  }, [])

  // Generate sort options for the data table
  const dataTableSortOptions = useMemo(() => {
    return {
      sortField: sort?.sortField,
      sortOrder: sort?.sortOrder
    }
  }, [sort, props?.configuration])
  // ========== END SORT ==========
  // ========================================
  // ========== START SELECTABLE ==========
  const columnSelectionMode = () => {
    if (isSelectable) {
      return (
        <Column selectionMode={selectableOptions()?.selectionMode} headerStyle={{ width: '3rem' }}></Column>
      )
    }
  }

  const isSelectable = useMemo(() => {
    return props?.configuration?.selectable?.enabled;
  }, [props?.configuration?.selectable])

  const onSelectionChange = useCallback(event => {
    let recordsCopy = [...records.data || []];
    let selectedRecords = [...selectableOptions()?.selection || []];

    let { value, type } = event;
    let { checked } = event.originalEvent;

    let _selectedRecords = [...selectedRecords || []];

    if (type == 'all' && checked) {
      _.each(value, record => {
        _selectedRecords.push(record);
      })
    } else if (type == 'all' && !checked) {
      _.remove(_selectedRecords, record => {
        return _.includes(_.map(recordsCopy, 'id'), record.id);
      });
    } else {
      _selectedRecords = value;
    }

    let onSelectionChangeCallback = props?.configuration?.selectable?.onSelectionChange;

    if (_.isFunction(onSelectionChangeCallback)) {
      onSelectionChangeCallback(_.uniqBy(_selectedRecords, 'id'))
    }
  }, [records, props]);

  // Y luego úsalo en selectableOptions:
  const selectableOptions = () => {
    if (isSelectable) {
      const { mode, selection } = props?.configuration?.selectable || {};

      return {
        selection: selection || [],
        selectionMode: mode || 'multiple',
        onSelectionChange: onSelectionChange
      }
    }
  }

  // ========== END SELECTABLE ==========
  // ========== START DATA TABLE TOOLBAR ==========
  const dataTableToolbar = () => {
    return (
      <div className="d-flex justify-content-end mb-3">
        {multiSelectColumns()}
        {exportButtons()}
      </div>
    )
  }
  // ========== END DATA TABLE TOOLBAR ==========
  // ========== START MULTI SELECT ==========
  const multiSelectColumns = () => {
      if (columnSelectable?.enabled) {
      return (
        <MultiSelect
          value={columnSelectable?.selection}
          options={columnSelectable?.columns}
          optionLabel="header"
          optionValue="field"
          onChange={columnSelectable?.onSelectionChange}
          display="chip"
          maxSelectedLabels={3}
          selectedItemsLabel="{0} columnas seleccionadas"
          filter
          placeholder={'columnas visibles'}
          className="w-full md:w-20rem"
        />
      )
    }
  }
  // ========== END MULTI SELECT ==========

  // ========== START EXPORT BUTTONS ==========
  const exportButtons = () => {
    return (
      <div>
        {exportZipButton()}
        {exportXlsxButton()}
        {typeof customAction === 'function' ? customAction(records.data) : customAction}
      </div>
    )
  }
  // ========== END EXPORT BUTTONS ==========
  // ========== START EXPORT ZIP BUTTON ==========
  const exportZipButton = () => {
    if (exportZip?.enabled) {
      const requestParams = {
        ...fetchRecordDefaultRequestParams,
        ids: _.map(props?.configuration?.selectable?.selection, 'id'),
        format: 'zip'
      }

      return (
        <Button className="ml-2" type="button" rounded severity="info" onClick={() => (window.location.href = exportZip?.endpoint({ ...requestParams }))} data-pr-tooltip="ZIP">
          <i className="fas fa-file-archive"></i>
        </Button>
      )
    }
  }
  // ========== END EXPORT ZIP BUTTON ==========
  // ========== START EXPORT EXCEL BUTTON ==========
  const exportXlsxButton = () => {
    if (exportXlsx?.enabled) {
      const requestParams = {
        ...fetchRecordDefaultRequestParams,
        ids: _.map(props?.configuration?.selectable?.selection, 'id'),
        columns_attributes: _.map(columns, 'field'),
        format: 'xlsx'
      }

      return (
        <Button className="ml-2" type="button" rounded severity="success" onClick={() => (window.location.href = exportXlsx?.endpoint({ ...requestParams }))} data-pr-tooltip="Excel">
          <i className="fas fa-file-excel"></i>
        </Button>
      )
    }
  }
  // ========== END EXPORT EXCEL BUTTON ==========
  // Generate the first option for the data table
  const firstOption = useMemo(() => {
    // la key 'first' indica la pagina actual
    // La pagina actual se basa en la cantidad de registros a mostrar por pagina y la pagina actual

    // // Página 1 (0-based)
    // first: 0, rows: 10 → página: 0
    // // Registros mostrados: 1-10

    // // Página 2 (0-based)
    // first: 10, rows: 10 → página: 1
    // // Registros mostrados: 11-20

    // // Página 3 (0-based)
    // first: 20, rows: 10 → página: 2
    // // Registros mostrados: 21-30

    const currentPage = records.currentPage;
    const perPage = queryFilterParams.per_page;

    let first = (currentPage * perPage) - 1;
    return first;

  }, [records.currentPage, queryFilterParams.per_page])

  // Generate the configuration for the data table
  const configuration = useMemo(() => {
    // Obtener las keys permitidas del objeto de configuración
    const filteredConfig = Object.fromEntries(
      Object.entries(props?.configuration || {}).filter(
        ([key]) => ALLOWED_KEYS.includes(key)
      )
    );

    let showGridlines = props?.configuration?.showGridlines;
    let stripedRows = props?.configuration?.stripedRows;

    if (showGridlines == null || showGridlines == undefined) {
      showGridlines = false;
    }

    if (stripedRows == null || stripedRows == undefined) {
      stripedRows = false;
    }

    if (stripedRows == false && showGridlines == false) {
      showGridlines = true;
    }

    return {
      ...filteredConfig,
      ...{
        filterDisplay: props?.configuration?.filterDisplay || 'menu',
        showGridlines: props?.configuration?.showGridlines,
        stripedRows: props?.configuration?.stripedRows,
        removableSort: props?.configuration?.removableSort || true,
        emptyMessage: emptyMessage
      }
    }
  }, [props?.configuration])

  // Generate the columns for the data table
  const dataTableColumns = useMemo(() => {
    return columns.map(column => {

      const style = { minWidth: '275px' }

      return (
        <Column
          field={column.field}
          header={column.header}
          body={column.body}
          key={_.snakeCase(column.field)}
          {...filterOptions(column)}
          {...sortOptions(column)}
          style={style}
        />
      )
    })
  }, [columns])

  // ========== START DISTRIBUTION LIST FILTER ==========
  const distributionListFilter = () => {
    let showDistributionListFilter = props?.configuration?.showDistributionListFilter;

    if (showDistributionListFilter == undefined || showDistributionListFilter == null) {
      showDistributionListFilter = true;
    }

    if (showDistributionListFilter) {
      return (
        <DistributionListFilter
          dataTable={dataTable}
          filters={filters}
          distributionList={distributionList}
          showAllRecordsRef={showAllRecordsRef}
          selectedRecords={props?.configuration?.selectable?.selection}
          callbacks={{
            onApplyFilters: applyFilters,
            onSetDistributionList: setDistributionList
          }}
          configuration={{
            context: props?.configuration?.context,
          }}
        />
      )
    }
  }
  // ========== END DISTRIBUTION LIST FILTER ==========
  // ========================================
  // ========== START DATA TABLE ==========

  // ========== END DATA TABLE ==========

  const dataTable = useRef(null);

  return (
    <div className='controldoc-datatable'>
      {distributionListFilter()}
      {dataTableToolbar()}

      <DataTable
        lazy
        header={header}
        onPage={onPage}
        onSort={onSort}
        value={records.data}
        totalRecords={records.total}
        rows={queryFilterParams.per_page}
        first={firstOption}
        dataKey="id"
        paginator
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        scrollable
        scrollHeight='1000px'
        // scrollHeight="600px"
        tableStyle={{ minWidth: '1000pxre' }}
        filters={filters}
        onFilter={onFilter}
        ref={dataTable}
        {...configuration}
        {...dataTableSortOptions}
        {...selectableOptions()}
        columns={columns}
      >
        {columnSelectionMode()}
        {dataTableColumns}
      </DataTable>
    </div>
  );
};



export default React.memo(ControldocDataTable);
